import {Alert, Badge, Button, Separator, Table, TableRow} from 'platform/components';
import {Box, Heading, HStack, Icon, Show, Space, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {
  getCheckoutProfileErrors,
  PreviewCheckoutProfileRuleArgSchema,
  usePreviewCheckoutProfileRuleMutation,
  type CheckoutProfileRulePreview,
  type CheckoutProfileRuleWrite,
  type CheckoutProfileError,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import type {ApiError} from 'shared/web';

import {getCheckoutProfileIssueMessage} from '../../../utils/getCheckoutProfileIssueMessage';
import {CheckoutRuleFeedback} from './CheckoutRuleFeedback';

interface Props {
  values: CheckoutProfileRuleWrite;
  ruleId?: string;
}

export function CheckoutRulePreview(props: Props) {
  return <CheckoutRulePreviewContent key={JSON.stringify(props)} {...props} />;
}

function CheckoutRulePreviewContent(props: Props) {
  const [preview, {isLoading}] = usePreviewCheckoutProfileRuleMutation();
  const [result, setResult] = useState<CheckoutProfileRulePreview>();
  const [errors, setErrors] = useState<CheckoutProfileError[]>([]);
  const request = PreviewCheckoutProfileRuleArgSchema.safeParse({
    body: {...props.values, id: props.ruleId},
  });

  const hasSelectedCondition = Object.values(props.values.conditions).some(
    (values) => (values?.length ?? 0) > 0
  );

  const currentResult = isLoading ? undefined : result;
  const isServiceOrder = props.values.sourceType === 'service-order';
  const externalLabel = i18n.t('entity.checkout.labels.winningRuleExternal');
  const internalLabel = i18n.t('entity.checkout.labels.winningRuleInternal');
  const externalWins =
    currentResult?.matchingVariants.filter((variant) => variant.winningRuleDefault?.isThisRule)
      .length ?? 0;

  const internalWins =
    currentResult?.matchingVariants.filter((variant) => variant.winningRuleInternal?.isThisRule)
      .length ?? 0;

  const columns = [
    isServiceOrder
      ? i18n.t('entity.order.labels.serviceOrderVariant')
      : i18n.t('page.warehouse.labels.directSaleVariant'),
    isServiceOrder
      ? i18n.t('entity.serviceOrder.labels.serviceOrderType')
      : i18n.t('entity.checkout.labels.directSaleTypes'),
    isServiceOrder
      ? i18n.t('entity.settings.labels.serviceCaseType')
      : i18n.t('entity.checkout.labels.directSaleAuthorizationProfile'),
    externalLabel,
    internalLabel,
  ].map((label) => ({
    width: '20%' as const,
    element: (
      <Box padding={3}>
        <Text size="xSmall" color="secondary" align="left">
          {label}
        </Text>
      </Box>
    ),
  }));

  const handlePreview = () => {
    if (!request.success || !hasSelectedCondition || isLoading) {
      return;
    }

    setResult(undefined);
    setErrors([]);
    void preview(request.data)
      .unwrap()
      .then(setResult)
      .catch((error: ApiError) => {
        const refusal = getCheckoutProfileErrors(error);

        if (refusal) {
          setErrors(
            refusal.errors.map((issue) => ({
              ...issue,
              message: getCheckoutProfileIssueMessage(issue.code, issue.message),
            }))
          );
        } else {
          handleApiError(error);
        }
      });
  };

  return (
    <VStack>
      <Separator />
      <Heading size={4}>{i18n.t('entity.checkout.labels.ruleResolution')}</Heading>
      <Space vertical={1} />
      <Text size="small" color="secondary">
        {i18n.t('entity.checkout.labels.ruleResolutionDescription')}
      </Text>
      <Show when={currentResult}>
        {(data) => (
          <>
            <Space vertical={4} />
            <Show when={data.conflicts.length === 0}>
              <Badge
                variant="subtle"
                colorScheme="green"
                value={i18n.t('entity.checkout.labels.noRuleConflict')}
              />
            </Show>
            <Show when={data.conflicts.length > 0}>
              <Alert
                variant="error"
                title={i18n.t('entity.checkout.labels.ruleConflicts')}
                message={data.conflicts.map((conflict) => conflict.name).join(', ')}
              />
            </Show>
          </>
        )}
      </Show>
      <Separator />
      <HStack justify="space-between" align="center" spacing={4}>
        <VStack>
          <Heading size={4}>{i18n.t('entity.checkout.labels.matchingVariants')}</Heading>
          <Space vertical={1} />
          <Text size="small" color="secondary">
            {i18n.t('entity.checkout.labels.matchingVariantsDescription')}
          </Text>
        </VStack>
        <Button
          variant="secondary"
          title={i18n.t('entity.documentTemplateEditor.shortcuts.labels.find')}
          onClick={handlePreview}
          isDisabled={!request.success || !hasSelectedCondition || isLoading}
          isLoading={isLoading}
        />
      </HStack>
      <Space vertical={4} />
      <CheckoutRuleFeedback errors={errors} />
      <Show when={currentResult}>
        {(data) => (
          <>
            <section aria-label={i18n.t('entity.checkout.labels.thisRule')}>
              <HStack spacing={4} align="center" wrap>
                <Text size="small">{i18n.t('entity.checkout.labels.thisRule')}</Text>
                <Badge
                  variant="subtle"
                  colorScheme="neutral"
                  value={`${externalLabel}: ${externalWins}/${data.matchingVariants.length}`}
                />
                <Badge
                  variant="subtle"
                  colorScheme="neutral"
                  value={`${internalLabel}: ${internalWins}/${data.matchingVariants.length}`}
                />
              </HStack>
            </section>
            <Space vertical={4} />
          </>
        )}
      </Show>
      <Table columns={columns} tableLayout="fixed">
        {currentResult?.matchingVariants.map((variant) => (
          <TableRow key={variant.variantId}>
            <Box padding={3}>
              <Text size="small">{variant.variantName}</Text>
            </Box>
            <Box padding={3}>
              <Text size="small">{variant.typeName}</Text>
            </Box>
            <Box padding={3}>
              <Text size="small">
                {isServiceOrder ? variant.serviceCaseTypeName : variant.authorizationProfileName}
              </Text>
            </Box>
            <CheckoutRulePreviewWinner winner={variant.winningRuleDefault} />
            <CheckoutRulePreviewWinner winner={variant.winningRuleInternal} />
          </TableRow>
        ))}
      </Table>
      <Show when={currentResult && currentResult.matchingVariants.length === 0}>
        <Box padding={3}>
          <Text size="small" color="secondary">
            {i18n.t('entity.checkout.labels.noMatchingVariants')}
          </Text>
        </Box>
      </Show>
    </VStack>
  );
}

function CheckoutRulePreviewWinner(props: {
  winner: CheckoutProfileRulePreview['matchingVariants'][number]['winningRuleDefault'];
}) {
  const status = match(props.winner)
    .with(null, () => ({
      icon: 'alert/warning' as const,
      color: 'severity.warning' as const,
      label: i18n.t('entity.checkout.labels.noWinningRule'),
    }))
    .with({isThisRule: true}, () => ({
      icon: 'action/check_circle' as const,
      color: 'severity.success' as const,
      label: i18n.t('entity.checkout.labels.thisRule'),
    }))
    .otherwise((winner) => ({
      icon: 'content/remove' as const,
      color: 'text.secondary' as const,
      label: winner.name,
    }));

  return (
    <Box padding={3}>
      <HStack spacing={2} align="center">
        <span aria-hidden="true">
          <Icon value={status.icon} color={status.color} size={5} />
        </span>
        <Text size="small">{status.label}</Text>
      </HStack>
    </Box>
  );
}
